import { useTheme } from '@approvalmax/theme';
import { Button, Flex, StepperLines, Text } from '@approvalmax/ui/src/components';
import { browserHelpers, hooks } from '@approvalmax/utils';
import { memo, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useMedia, useMount } from 'react-use';
import { amplitudeService } from 'services/amplitude';
import { useRegister } from 'shared/data/webApp/v1';
import { useResendTimeout } from 'shared/hooks/useResendTimeout';
import { getPath, Path, Workspace } from 'urlBuilder';

import { emailServices, resendTimeout, resendTimeoutStorageKey } from './Content.constants';
import { sendChangeEmailEvent } from './Content.helpers';
import { messages } from './Content.messages';
import { EmailLink } from './Content.styles';

export const Content = memo(() => {
    const history = useHistory();

    const [app] = hooks.useQueryParam<Workspace>('app');
    const [email] = hooks.useQueryParam('email');
    const [consentMarketingAccepted] = hooks.useQueryParam('consentMarketingAccepted');

    const registerPath = getPath(Path.register, {
        email,
        app,
        consentMarketingAccepted: Boolean(consentMarketingAccepted),
    });

    const { startTimer, isTimerActive, formattedResendTime } = useResendTimeout(resendTimeoutStorageKey, resendTimeout);

    const { mutate } = useRegister();

    const resend = useCallback(() => {
        amplitudeService.sendData('signup: click on resend button');

        mutate({
            body: {
                email,
                acceptedProductNewsEmails: Boolean(consentMarketingAccepted),
                uiState: encodeURIComponent(JSON.stringify({ app })),
                sendConfirmationLink: true,
                timeZoneForEmail: window.ApprovalMax.userTimeZone,
                isMobileFlow: browserHelpers.isMobile(),
            },
        });
        startTimer();
    }, [app, consentMarketingAccepted, email, mutate, startTimer]);

    useMount(() => {
        if (!email) {
            history.replace(registerPath);
        }
    });

    const { theme } = useTheme();
    const xsmallViewport = useMedia(`(max-width: ${theme.container.viewport.xsmall})`);
    const xxsmallViewport = useMedia(`(max-width: ${theme.container.viewport.xxsmall})`);

    return (
        <Flex direction='column' spacing='24'>
            <StepperLines currentStep={1} stepsCount={3} />

            <Flex direction='column' spacing='12' justifyContent='center'>
                <Text font='label' fontSize='large' textAlign='center' color='midnight80'>
                    {messages.linkSent}
                </Text>

                <Text
                    font='headline'
                    fontSize={xsmallViewport || xxsmallViewport ? 'xxsmall' : 'xsmall'}
                    fontWeight='medium'
                    textAlign='center'
                    color='midnight80'
                >
                    {email}
                </Text>

                <Text font='label' fontSize='large' textAlign='center' color='midnight80'>
                    {messages.clickToVerify}
                </Text>
            </Flex>

            <Flex justifyContent='center'>
                <Button size='small' color='blue10' as={Link} to={registerPath} onClick={sendChangeEmailEvent}>
                    {messages.changeEmail}
                </Button>
            </Flex>

            <Flex container wrap={xxsmallViewport}>
                {emailServices.map(({ name, url, onClick, Logo }) => (
                    <Flex size={xxsmallViewport ? 24 : 8} key={name}>
                        <EmailLink
                            font='label'
                            fontSize='small'
                            textAlign='center'
                            external
                            href={url}
                            onClick={onClick}
                        >
                            <Logo height={24} />

                            {name}
                        </EmailLink>
                    </Flex>
                ))}
            </Flex>

            <Flex direction='column' spacing='4' justifyContent='center'>
                <Text font='label' fontSize='small' fontWeight='medium' textAlign='center' color='midnight80'>
                    {messages.findEmail}
                </Text>

                <Text font='label' fontSize='small' textAlign='center' color='midnight80'>
                    {messages.checkEmailAndSpam}
                </Text>
            </Flex>

            <Flex justifyContent='center'>
                <Button color='blue80' onClick={resend} disabled={isTimerActive}>
                    {isTimerActive ? messages.resendIn({ time: formattedResendTime }) : messages.resend}
                </Button>
            </Flex>
        </Flex>
    );
});

Content.displayName = 'Content';
